import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useNotes } from "@/hooks/use-notes";
import { Loader2, Plus, Pencil, X, Check, Trash2 } from "lucide-react";
import { useState, useMemo } from "react";
import { format } from "date-fns";

interface NotesPanelProps {
    claimId: number;
    canWrite: boolean;
}

export function NotesPanel({ claimId, canWrite }: NotesPanelProps) {
    const { notes, isLoading, createNote, isCreating, updateNote, isUpdating, deleteNote } = useNotes(claimId);
    const [newNote, setNewNote] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editText, setEditText] = useState("");
    const [deleteNoteId, setDeleteNoteId] = useState<number | null>(null);

    const handleCreateNote = () => {
        if (!canWrite) return;
        if (!newNote.trim()) return;
        createNote(newNote.trim(), {
            onSuccess: () => {
                setNewNote("");
                setIsAdding(false);
            }
        });
    };

    const handleSaveEdit = (noteId: number) => {
        if (!canWrite) return;
        if (!editText.trim()) return;
        updateNote({ noteId, text: editText.trim() }, {
            onSuccess: () => {
                setEditingNoteId(null);
                setEditText("");
            }
        });
    };

    const sortedNotes = useMemo(() => {
        return [...(notes || [])].sort((a, b) => {
            const dateA = a.updatedAt && new Date(a.updatedAt).getTime() !== new Date(a.createdAt).getTime()
                ? new Date(a.updatedAt).getTime()
                : new Date(a.createdAt).getTime();
            const dateB = b.updatedAt && new Date(b.updatedAt).getTime() !== new Date(b.createdAt).getTime()
                ? new Date(b.updatedAt).getTime()
                : new Date(b.createdAt).getTime();
            return dateB - dateA;
        });
    }, [notes]);

    return (
        <ScrollArea className="h-full w-full">
            <div className="p-6">
                <div className="mb-4">
                    <div>
                        <h2 className="text-xl font-semibold">Notes</h2>
                        <p className="text-sm text-muted-foreground mt-1">Add and manage claim notes</p>
                    </div>
                </div>

                {!isAdding && canWrite && (
                    <button
                        onClick={() => setIsAdding(true)}
                        className="w-full mb-4 py-3 border-2 border-dashed border-muted-foreground/25 rounded-lg text-sm text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Add Note
                    </button>
                )}

                {isAdding && (
                    <div className="mb-6 space-y-2">
                        <Textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Write a note..."
                            disabled={!canWrite || isCreating}
                            className="min-h-[80px] resize-none"
                            autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => { setIsAdding(false); setNewNote(""); }} disabled={!canWrite || isCreating}>Cancel</Button>
                            <Button size="sm" onClick={handleCreateNote} disabled={!canWrite || isCreating || !newNote.trim()}>
                                {isCreating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                Save
                            </Button>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>
                ) : sortedNotes.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">No notes yet</div>
                ) : (
                    <div className="space-y-4">
                        {sortedNotes.map((note) => (
                            <div
                                key={note.id}
                                className="bg-card border rounded-lg p-4 shadow-sm w-full group"
                            >
                                {editingNoteId === note.id ? (
                                    <div className="space-y-2">
                                        <Textarea
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            disabled={!canWrite || isUpdating}
                                            className="min-h-[80px] resize-none"
                                            autoFocus
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <Button size="sm" variant="ghost" onClick={() => { setEditingNoteId(null); setEditText(""); }} disabled={!canWrite || isUpdating}>
                                                <X className="h-3.5 w-3.5 mr-1" /> Cancel
                                            </Button>
                                            <Button size="sm" onClick={() => handleSaveEdit(note.id)} disabled={!canWrite || isUpdating || !editText.trim()}>
                                                {isUpdating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-sm whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{note.text}</p>
                                        <div className="flex items-center justify-between mt-2">
                                            <span className="text-xs text-muted-foreground">
                                                {note.createdByName && <span className="font-medium">{note.createdByName} · </span>}
                                                {note.updatedAt && new Date(note.updatedAt).getTime() !== new Date(note.createdAt).getTime()
                                                    ? `Edited ${format(new Date(note.updatedAt), "MMM d, h:mm a")}${note.updatedByName ? ` by ${note.updatedByName}` : ''}`
                                                    : format(new Date(note.createdAt), "MMM d, h:mm a")}
                                            </span>
                                            {canWrite && (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => { setEditingNoteId(note.id); setEditText(note.text); }}
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                                                        title="Edit note"
                                                        aria-label="Edit note"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteNoteId(note.id)}
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                                        title="Delete note"
                                                        aria-label="Delete note"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <AlertDialog open={deleteNoteId !== null} onOpenChange={(open) => { if (!open) setDeleteNoteId(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Note</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this note? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { if (canWrite && deleteNoteId !== null) { deleteNote(deleteNoteId); setDeleteNoteId(null); } }}
                            disabled={!canWrite}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ScrollArea>
    );
}
